<template>
  <div
    id="i-cropper"
    class="full"
  />
</template>

<script>
import Croppie from 'croppie'

export default {
  name: 'ICropper',

  props: {
    boundary: {
      type: Object,
      default: () => ({})
    },
    customClass: {
      type: String,
      default: ''
    },
    enableExif: {
      type: Boolean,
      default: false
    },
    enableOrientation: {
      type: Boolean,
      default: false
    },
    enableResize: {
      type: Boolean,
      default: false
    },
    enableZoom: {
      type: Boolean,
      default: true
    },
    enforceBoundary: {
      type: Boolean,
      default: true
    },
    mouseWheelZoom: {
      type: [Boolean, String],
      default: true
    },
    showZoomer: {
      type: Boolean,
      default: true
    },
    viewport: {
      type: Object,
      default: () => ({
        width: 100,
        height: 100,
        type: 'square'
      })
    },
    viewportThirdsLine: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      croppie: null,
      zoomMin: 0, // zoom 最小值，會依照圖片原始大小有不同結果，算法： 100 / 最小邊長，四捨五入至小數後第四位
      zoomMax: 10, // zoom 最大值，給定固定值 10，會影響放大到最大時，Viewport 中的像素數。例：原圖 500*500，放大至最大時，Viewport 中會是 50*50 (500/{zoomMax})
      zoomInterval: 10, // zoom 最大值與最小值之差
      zoomPercent: 0, // 縮放比例 0 ~ 100
      originImageWidth: 0, // 圖片原始寬
      originImageHeight: 0 // 圖片原始高
    }
  },

  mounted () {
    this.initCroppie()
  },

  methods: {
    initCroppie () {
      const el = document.getElementById('i-cropper')
      const options = {
        maxZoom: this.zoomMax,
        enableExif: this.enableExif,
        enableOrientation: this.enableOrientation,
        enableResize: this.enableResize,
        enableZoom: this.enableZoom,
        enforceBoundary: this.enforceBoundary,
        mouseWheelZoom: this.mouseWheelZoom,
        showZoomer: this.showZoomer,
        viewport: this.viewport
      }
      if (this.boundary) {
        options.boundary = this.boundary
      }
      this.croppie = new Croppie(el, options)
      el.addEventListener('update', (ev) => {
        this.onUpdate(ev)
      })
      // Draw the thirds line
      if (this.viewportThirdsLine) {
        const viewport = el.getElementsByClassName('cr-viewport')[0]
        const thirdsLineClassName = [
          'cropper-thirds-line-horizontal',
          'cropper-thirds-line-vertical'
        ]
        thirdsLineClassName.forEach(className => {
          const newLine = document.createElement('div')
          newLine.classList.add('cropper-thirds-line', className)
          viewport.appendChild(newLine)
        })
      }
      this.$emit('cropperReady')
    },

    onUpdate (ev) {
      const detail = ev.detail
      this.zoomPercent = Math.floor((detail.zoom - this.zoomMin) / (this.zoomMax - this.zoomMin) * 100)
      const points = detail.points.map(point => {
        return parseInt(point, 10)
      })
      const updateData = {
        x: points[0],
        y: points[1],
        width: points[2] - points[0],
        height: points[3] - points[1],
        percent: this.zoomPercent
      }
      this.$emit('update', updateData)
    },

    bind (options) {
      this.setImageSize(options.url)
      return this.croppie.bind(options)
    },

    getCropArea () {
      const points = this.croppie.get().points.map(point => {
        return parseInt(point)
      })
      return {
        x: points[0],
        y: points[1],
        width: points[2] - points[0],
        height: points[3] - points[1],
        percent: this.zoomPercent
      }
    },

    setImageSize (imageUrl) {
      const img = new Image()
      img.addEventListener('load', () => {
        this.originImageWidth = img.naturalWidth
        this.originImageHeight = img.naturalHeight
        this.zoomMin = Math.round(Math.min(this.viewport.width, this.viewport.height) / img.naturalWidth * 10000) / 10000
        this.zoomInterval = this.zoomMax - this.zoomMin
      })
      img.src = imageUrl
    },

    setZoomViaPercent (percent) {
      const difference = this.zoomInterval * percent / 100
      const zoomResult = this.zoomMin + difference
      this.croppie.setZoom(zoomResult)
    },

    destroy () {
      this.croppie.destroy()
    },

    reset () {
      this.croppie.destroy()
      this.initCroppie()
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~css/addon/colors'
@import '~css/addon/main'
$cropper-thirds-line-style = 1px solid rgba($white,.55)

#i-cropper {
  background-color: black
}

.cr-viewport {
  overflow: hidden
}

.cropper-thirds-line {
  position: absolute
  z-index: 1
}

.cropper-thirds-line-horizontal {
  width: 100%
  height: 33.3333%
  transform: translateY(100%)
  border-top: $cropper-thirds-line-style
  border-bottom: $cropper-thirds-line-style
}

.cropper-thirds-line-vertical {
  width: 33.3333%
  height: 100%
  transform: translateX(100%)
  border-left: $cropper-thirds-line-style
  border-right: $cropper-thirds-line-style
}
</style>

<style src="croppie/croppie.css"></style>
